import * as React from "react";

import { Entry } from "../models/entries";
import { User } from "../models/users";
import EntryStats from "../Entry/EntryStats";
import { PlaySong } from "../Toolbar/PlaySong";
import { RLink } from "./RLink";
import { AvatarStackUser } from "./AvatarStack";
import { TestIds } from "../testIds";

export function EntryTitle({
  entry,
  user,
  hidden,
  hasFavorited,
  showCollabButton,
  entryUsers,
  compoReleased,
}: {
  entry: Entry;
  user: User | undefined;
  hidden: boolean;
  hasFavorited: boolean;
  showCollabButton: boolean;
  entryUsers: User[];
  compoReleased: boolean;
}): JSX.Element {
  const showHiddenTitle = hidden && user && user.admin;
  const isSong = entry.type === "song";

  // Determine if we should show avatars or anonymous chip
  const shouldShowAnonymous = entry.anonymous && !compoReleased;
  const avatarUsers: AvatarStackUser[] = entryUsers
    .filter((u) => u.id !== -1)
    .map((u) => ({
      username: u.username,
      avatar_url: u.avatar_url,
    }));

  // Compute artists array for playback display
  const artistsForPlayback = shouldShowAnonymous
    ? undefined
    : avatarUsers.length > 0
      ? avatarUsers.map((u) => u.username)
      : undefined;

  const renderAvatars = () => {
    if (shouldShowAnonymous || avatarUsers.length === 0) {
      return (
        <div className="flex flex-col items-center gap-1">
          <img
            className="h-[80px] w-[80px] rounded object-cover"
            src="/img/anonymous-chip.png"
            alt="Anonymous Chip"
          />
          <span className="text-sm text-slate-600 dark:text-slate-300">
            Anonymous Chip
          </span>
        </div>
      );
    }

    if (avatarUsers.length === 1) {
      return (
        <RLink
          to={`/user/${avatarUsers[0].username}`}
          className="flex flex-col items-center gap-1"
        >
          <img
            className="h-[80px] w-[80px] rounded object-cover"
            src={avatarUsers[0].avatar_url}
            alt={avatarUsers[0].username}
          />
          <span className="text-sm text-slate-600 dark:text-slate-300">
            {avatarUsers[0].username}
          </span>
        </RLink>
      );
    }

    // Multiple collaborators - show as a row
    return (
      <div className="flex items-start gap-3">
        {avatarUsers.map((u) => (
          <RLink
            key={u.username}
            to={`/user/${u.username}`}
            className="flex flex-col items-center gap-1"
          >
            <img
              className="h-[60px] w-[60px] rounded object-cover transition hover:scale-105"
              src={u.avatar_url}
              alt={u.username}
            />
            <span className="max-w-[60px] truncate text-xs text-slate-600 dark:text-slate-300">
              {u.username}
            </span>
          </RLink>
        ))}
      </div>
    );
  };

  return (
    <div className="pb-8 text-left">
      {/* Row 1: Play + Title + Avatars */}
      <div className="flex items-center gap-4">
        {/* Play button */}
        {isSong && entry.uploaded_url && (
          <div className="flex-shrink-0">
            <PlaySong entry={entry} size="large" artists={artistsForPlayback} />
          </div>
        )}

        {/* Title (and stats on desktop) */}
        <div className="min-w-0 flex-1">
          <h1
            className="text-2xl font-semibold leading-tight text-[#333] dark:text-white md:text-4xl lg:text-5xl break-words line-clamp-2 md:line-clamp-3"
            data-testid={TestIds.EntryTitleHeading}
          >
            {showHiddenTitle ? (
              <span className="text-slate-400 dark:text-white/40">
                {entry.title}
              </span>
            ) : (
              entry.title
            )}
          </h1>

          {isSong && !entry.uploaded_url && (
            <div className="mt-2">
              <RLink
                className="text-sm font-semibold uppercase tracking-wide text-chips-gold transition hover:text-amber-400"
                to={entry.url}
              >
                Listen to the song
              </RLink>
            </div>
          )}

          {/* Desktop: Stats inline with title */}
          <div className="hidden md:block">
            <EntryStats
              entry={entry}
              user={user}
              faved={hasFavorited}
              showCollabButton={showCollabButton}
              variant="hero"
            />
          </div>
        </div>

        {/* Avatars - right aligned */}
        <div className="ml-auto flex-shrink-0">{renderAvatars()}</div>
      </div>

      {/* Mobile: Stats on separate row */}
      <div className="mt-3 md:hidden">
        <EntryStats
          entry={entry}
          user={user}
          faved={hasFavorited}
          showCollabButton={showCollabButton}
          variant="hero"
        />
      </div>
    </div>
  );
}
